import { h, Component, Prop } from '@stencil/core';
import { r } from '../root/helper';
import Project from '../root/project';


function createLocale(date) {
  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

  let dateObj = new Date(date);
  return `${monthNames[dateObj.getMonth()]} ${dateObj.getDate()}, ${dateObj.getFullYear()}`
}

@Component({
  tag: 'tip-meeting'
})
export class Meeting {
  @Prop() label: string = 'Meeting';
  @Prop({mutable: true}) projects: any[];

  componentWillLoad() {
    this.projects =
      document.querySelector('tip-root').projectManager.getCompleteProjects();
  }

  getUniqueDate(field) {
    let uniqueDates = []
    for (let project of this.projects) {
      if (project.getLatestAttribute(field) !== null &&
          uniqueDates.indexOf(project.getLatestAttribute(field)) === -1) {
        uniqueDates = uniqueDates.concat(project.getLatestAttribute(field))
      }
    }
    return uniqueDates
  }

  getProjects(field: string, type: 'Modification' | 'Amendment', date: string) {
    let filteredProjects =  this.projects.filter((project) => {
      return project.getLatestAttribute(field) === date &&
      project.getLatestAttribute('change_type') === type
    })
    filteredProjects.sort(function(a, b) {
      let textA = a.getLatestAttribute('project_id');
      let textB = b.getLatestAttribute('project_id');
      return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
    })
    return filteredProjects
  }

  getChangesPlural(count, type) {
    if (type === 'amendment') return (count == 1 ? 'amendment' : 'amendments');
    return (count == 1 ? 'modification' : 'modifications');
  }

  generateBackground(projects, meetingType) {
    let count = {}
    for (let project of projects) {
      count[project.getLatestAttribute('change_agency')] =
        (count[project.getLatestAttribute('change_agency')] || 0) + 1;
    }
    let background = [];
    for (let key in count) {
      background.push(
        `${key} submitted ${count[key]} ${this.getChangesPlural(count[key], meetingType)} to the Transportation Improvement Program FY 2020-2023. `
      );
    }
    background = background.map((item) => {
      return {text: item}
    })
    return background;
  }

  // This generates all rows with the same meeting date
  generateTable(project, meetingType, date) {
    let ignoreFields = [
      'date_notified', 'date_added', 'date_approved', 'date_technical_meeting',
      'date_policy_meeting', 'comments'
    ];
    let headerAttributes = ['project_title', 'fiscal_year', 'start_terminus', 'end_terminus'];
    let glColumns = Array.from(document.querySelectorAll('gl-table-column'));
    let meetingFeatures = project.getMemoRows(
      (meetingType === 'policy' ? 'date_policy_meeting' : 'date_technical_meeting'),
      date);
    let changeFields = [];
  
    for (let i = 0; i < meetingFeatures.length - 1; i++) {
      changeFields = changeFields.concat(
        Project.getFieldDifference(meetingFeatures[i], meetingFeatures[i + 1]));
    }
    headerAttributes = headerAttributes.concat(changeFields)
    headerAttributes = headerAttributes.filter((e, i) => {
      return headerAttributes.indexOf(e) >= i
    }).filter((e) => {
      return ignoreFields.indexOf(e) === -1;
    });

    let headerCol = headerAttributes.map((attr) => {
      let glCol = glColumns.find((col) => {
        return col['attribute'] === attr
      })
      return { text: glCol['label'], bold: true }
    });

    if (meetingFeatures.length > 1) {
      meetingFeatures.pop();
    }

    let dataRows = meetingFeatures.map((f) => {
      return headerAttributes.map((attr) => {
        let glCol = glColumns.find((col) => {
            return col['attribute'] === attr
        })
        return r(f['properties'][attr], glCol['format']);
      })
    })

    let table = {
      table: {
        body: [
          headerCol,
          dataRows[0]
        ]
      }
    }
    return table
  }

  generateProjectDetails(projects, meetingType, date) {
      let projectDetails = [];
      for ( let project of projects ) {
        let rows = project.getMemoRows(meetingType, date);
        let comments = rows.map((r) => {
          return {text: r['properties']['comments']}
        })

        projectDetails.push({
          unbreakable: true,
          stack: [{
              text: project.getLatestAttribute('project_id'),
              bold: true, italics: true, style: 'content', decoration: 'underline'
            },
            this.generateTable(project, meetingType, date),
            {text: (project.getLatestAttribute('description') === null) ?
            '' : 'Project Description: ' + project.getLatestAttribute('description'),
            style: 'p'
            },
            comments
            ]
          }
        )
      }
    return projectDetails
  }

  generateContent(
    projects,
    memoType: 'modification' | 'amendment',
    meetingType: 'policy' | 'technical',
    date: any) {
    if (memoType === 'modification') {
      return [
        {
          text: [
            { text: 'Background: ', bold: true },
            ...this.generateBackground(projects, memoType)
          ],
          style: 'content'
        },
        {
          text: [ 'Link to TIP Explorer: ',
            {
              text: 'https://ccrpc.org/tip',
              link: 'https://ccrpc.org/tip',
              decoration:"underline"
            }
          ],
          style: 'content'
        },
        { text: 'The following describes the changes: '},
        this.generateProjectDetails(projects, meetingType, date)
      ]
    }

    if (memoType === 'amendment') {
      return [
        {
          text: [
            { text: 'Background: ', bold: true },
            ...this.generateBackground(projects, memoType)
          ],
          style: 'content'
        },
        {
          text: [ 'Link to TIP Explorer: ',
            {
              text: 'https://ccrpc.org/tip',
              link: 'https://ccrpc.org/tip',
              decoration:"underline"
            }
          ],
          style: 'content'
        },
        { text: 'The following describes the proposed changes: '},
        this.generateProjectDetails(projects, meetingType, date),
        {
          text: [ {text: 'Staff Recommendation: ', bold: true},
            'Approve Transportation Improvement Program FY 2020-2023 Amendments'
          ],
          style: 'content'
        }
      ]
    }
  }

  render() {
    let policyDates = this.getUniqueDate('date_policy_meeting')
    .map((date) => {
      return {'raw': date,
              'locale': createLocale(date)}
    });
    let techDates = this.getUniqueDate('date_technical_meeting')
    .map((date) => {
      return {'raw': date,
              'locale': createLocale(date)}
    });;
    return (
      <ion-list>
        <ion-list-header>
          <ion-label><h1>Policy Meetings</h1></ion-label>
        </ion-list-header>
        {policyDates.map((date) =>
          <ion-item>
            <ion-label><h2>Policy Meeting: {date['locale']}</h2>
              <tip-presentation-button
                presentation-type={'policy_meeting'}
                presentation-date={date['raw']}>
              </tip-presentation-button>
              <tip-memo
                memo-title= {'Policy Meeting: ' + date['locale']}
                to="CUUATS Policy Committee Members"
                from="CUUATS Staff"
                re="Transportation Improvement Program FY 2020-2023, Modifications"
                date={date['locale']}
                requested-action=""
                content={
                  this.generateContent(
                    this.getProjects('date_policy_meeting', 'Modification', date['raw']),
                    'modification',
                    'policy',
                    date['raw']
                  )}
                label='Modifications'
                type='modification'>
              </tip-memo>
              <tip-memo
                memo-title= {'Policy Meeting: ' + date['locale']}
                to="CUUATS Policy Committee Members"
                from="CUUATS Staff"
                re="Transportation Improvement Program FY 2020-2023, Amendments"
                date={date['locale']}
                requested-action="Approve Transportation Improvement Program FY 2020-2023 Amendments"
                content={
                  this.generateContent(
                    this.getProjects('date_policy_meeting', 'Amendment', date['raw']),
                    'amendment',
                    'policy',
                    date['raw']
                  )}
                label='Amendments'
                type='amendment'>
              </tip-memo>
            </ion-label>
          </ion-item>
        )}
          <ion-list-header>
            <ion-label><h1>Technical Meetings</h1></ion-label>
          </ion-list-header>
          {techDates.map((date) =>
            <ion-item>
              <ion-label><h2>Technical Meeting: {date['locale']}</h2>
                <tip-presentation-button
                  presentation-type={'technical_meeting'}
                  presentation-date={date['raw']}>
                </tip-presentation-button>
                <tip-memo
                  memo-title={'Technical Meeting: ' + date['locale']}
                  to="CUUATS Technical Committee Members"
                  from="CUUATS Staff"
                  re="Transportation Improvement Program FY 2020-2023, Modifications"
                  requested-action=""
                  date={date['locale']}
                  content={
                    this.generateContent(
                      this.getProjects('date_technical_meeting', 'Modification', date['raw']),
                      'modification',
                      'technical',
                      date['raw']
                    )}
                  label='Modifications'
                  type='modification'>
                  </tip-memo>
                  <tip-memo
                    memo-title= {'Technical Meeting: ' + date['locale']}
                    to="CUUATS Technical Committee Members"
                    from="CUUATS Staff"
                    re="Transportation Improvement Program FY 2020-2023, Amendments"
                    date={date['locale']}
                    requested-action="Approve Transportation Improvement Program FY 2020-2023 Amendments"
                    content={
                      this.generateContent(
                        this.getProjects('date_technical_meeting', 'Amendment', date['raw']),
                        'amendment',
                        'technical',
                        date['raw']
                      )}
                    label='Amendments'
                    type='amendment'>
                  </tip-memo>
                </ion-label>
              </ion-item>
            )}
      </ion-list>
    )
  }
}
